import React, { forwardRef, useState, useRef, useImperativeHandle, useEffect, useMemo } from "react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import {
    Dropdown,
    TextInput,
    TextArea,
    Checkbox,
    MultiSelectionInput,
} from "colbi_web_ui/lib/components/inputs";
import { FORM_ACTIONS } from "../FormActions";
import { checkRoles } from "colbi_web_ui/lib/utils/roleValidator";
import styles from "./NcRuleForm.module.sass";
import { UserRoles } from "../../../enums/userRoles";
import { datetime } from "../../../utils/datetime";
import CheckPermission from "colbi_web_ui/lib/components/CheckPermission/CheckPermission";
import Icon from "colbi_web_ui/lib/components/Icon/Icon";
import locales from "../../../I18n/locales";
import { parseJson } from "../../../utils";
import PackageDropdownMultiSelector from "../Packages/PackageDropdownMultiSelector/PackageDropdownMultiSelector";
import { formatRuleContentValues } from "../../../utils/rulesContent";

const NcRuleForm = (props, ref) => {
    const { data, status, onChange, onSave, classifiers, querySelectors, packages } = props;
    const { i18n, user, locale: actualLocale } = useGlobalValue();
    const { severity, level, referenceType, appliesTo, appliesToVersion, functionType } =
        classifiers || {};

    const localesArray = locales();
    const {
        id,
        RuleID,
        RuleType,
        Severity: ruleSeverity,
        Description,
        DescriptionPreview,
        DescriptionDetail,
        CreatedAt,
        UpdatedAt,
        Metadata,
        Content,
        userDefined = 1,
        Config,
        Packages: selectedPackages,
    } = data || {};
    const { items: sqlSelectors } = querySelectors || {}
    const [metadataFormatted, setMetadataFormatted] = useState("{}");
    const [contentFormatted, setContentFormatted] = useState("{}");
    const [refs, setRefs] = useState({});
    const [scroll, setScroll] = useState(false)
    const [eventType, setEventType] = useState("")

    const parsedMetadata = jsonToArray(parseJson(metadataFormatted));
    const parsedContent = parseJson(contentFormatted);
    const parsedDescriptionByLocale = parseJson(Description) || Description;
    const parsedDescriptionDetailByLocale = parseJson(DescriptionDetail) || DescriptionDetail;
    const parsedDescriptionPreviewByLocale = parseJson(DescriptionPreview) || DescriptionPreview;
    const availableDescriptionLocales = availableLocales(localesArray, parsedDescriptionByLocale);

    const descriptionItems =
        statusCheck(status) &&
        buildRuleTranslationContainer(
            parsedDescriptionByLocale,
            availableDescriptionLocales,
            localesArray,
            parsedDescriptionByLocale,
            parsedDescriptionDetailByLocale,
            parsedDescriptionPreviewByLocale,
        );
    const parsedConfig = JSON.parse(Config || "[]");
    const usedAppliesTo = (parsedConfig || []).map(({ FileType }) => FileType);
    const availableAppliesTo = (appliesTo || []).filter(
        ({ id }) => !(usedAppliesTo || []).includes(id),
    );
    const { Entity: ruleReferenceType } = parsedConfig[0] || {};

    const ruleMetadataItemsComponent =
        statusCheck(status) && buildRuleMetadataTranslationContainer(parsedMetadata, localesArray);

    const ruleConfigurations = statusCheck(status) && buildConfigsDataContainer(parsedConfig, appliesTo);

    const ruleContentComponent = statusCheck(status) && buildRuleContentContainer(parsedContent, sqlSelectors, functionType || []);

    useEffect(() => {
        if (parsedMetadata && parsedMetadata.length) {
            setRefs((parsedMetadata || []).reduce((acc, _, index) => {
                acc[index] = React.createRef();
                return acc;
            }, {}));
        }
        if (eventType === "scrollToContainer") {
            setScroll(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadataFormatted]);

    useEffect(() => {
        if (scroll && eventType === "scrollToContainer") {
            scrollToItem(parsedMetadata.length - 1)
        }
        setScroll(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scroll])

    const scrollToItem = (id) => {
        return refs[id].current.scrollIntoView({ behavior: "smooth", block: "start" })
    };

    useMemo(() => {
        try {
            if (Content) {
                setContentFormatted(JSON.stringify(JSON.parse(Content)));
            }
        } catch (e) {
            console.log(e);
        }
    }, [Content]);

    useMemo(() => {
        try {
            if (Metadata) {
                setMetadataFormatted(JSON.stringify(JSON.parse(Metadata)));
            }
        } catch (e) {
            console.log(e);
        }
    }, [Metadata]);

    const disableInputs = checkRoles(user, [UserRoles.OWNER]) && userDefined === 0 ? true : false;

    const tabs = ["Headers", "Descriptions", "Commands", "Configurations", "Packages"];

    const formRef = useRef();
    const [selected, setSelected] = useState(0);

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const handleGlobalObjectChange = (e, value) => {
        const { Description, DescriptionDetail, DescriptionPreview, Metadata, Content } = value || {};
        if (value) {
            if (Metadata) {
                onChange &&
                    onChange({
                        originalEvent: e,
                        target: {
                            value: {
                                data: {
                                    ...data,
                                    Metadata: JSON.stringify(Metadata),
                                },
                            },
                        },
                    });
            } else if (Content) {
                onChange &&
                    onChange({
                        originalEvent: e,
                        target: {
                            value: {
                                data: {
                                    ...data,
                                    Content: JSON.stringify(Content),
                                },
                            },
                        },
                    });
            } else {
                onChange &&
                    onChange({
                        originalEvent: e,
                        target: {
                            value: {
                                data: {
                                    ...data,
                                    Description: JSON.stringify(Description),
                                    DescriptionDetail: JSON.stringify(DescriptionDetail),
                                    DescriptionPreview: JSON.stringify(DescriptionPreview),
                                },
                            },
                        },
                    });
            }
        }
    };

    const handleConfigsObjectChange = (e, key, value, index, array) => {
        array[index][`${key}`] = value;
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            Config: JSON.stringify([...(array || [])]),
                        },
                    },
                },
            });
    };

    return statusCheck(status) ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (formRef.current.checkValidity()) {
                    if (data && !data["Metadata"]) {
                        data["Metadata"] = "{}"
                    }
                    data["Content"] = formatRuleContentValues(contentFormatted);
                    data["Severity"] = parseInt(ruleSeverity);
                    data["userDefined"] = userDefined
                    if (userDefined === 0) {
                        data["Content"] = '{}'
                    }
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <div className={styles["tabs"]}>
                {(tabs || []).map((tabName, index) => (
                    <div
                        key={index}
                        className={`${styles["tab"]} ${
                            selected === index ? styles["is-selected"] : ""
                        }`}
                        onClick={() => {
                            setSelected(index);
                        }}
                    >
                        <small>{i18n`${tabName}`}</small>
                    </div>
                ))}
            </div>
            <div className={`${styles["inputs"]} ${styles[`visible-${selected}`]}`}>
                <div className={styles["headers"]}>
                    <div className={styles["input-field"]}>
                        <TextInput
                            className={styles["input"]}
                            name={i18n`Identificador`}
                            value={RuleID || ""}
                            placeholder={i18n`Enter an identificator`}
                            onChange={(e) => {
                                handleChange(
                                    e,
                                    disableInputs ? "disabled" : "RuleID",
                                    e.target.value,
                                );
                            }}
                        />
                    </div>
                    <div className={styles["input-field"]}>
                        <TextInput
                            className={styles["input"]}
                            name={i18n`RuleType`}
                            value={RuleType || ""}
                            placeholder={i18n`Enter a Rule Type`}
                            onChange={(e) => {
                                handleChange(
                                    e,
                                    disableInputs ? "disabled" : "RuleType",
                                    e.target.value,
                                );
                            }}
                        />
                    </div>
                    <div className={styles["input-field"]}>
                        <h5 className={styles["label"]}>{i18n`Severity`}</h5>
                        <Dropdown
                            value={
                                (ruleSeverity || ruleSeverity === 0) && severity.toString().length
                                    ? severity.find(
                                          ({ id }) => parseInt(id) === parseInt(ruleSeverity),
                                      ) || severity[0]
                                    : null
                            }
                            options={severity || []}
                            placeholder={""}
                            className={styles["dropdown"]}
                            onChange={(e) => {
                                handleChange(
                                    e,
                                    disableInputs ? "disabled" : "Severity",
                                    parseInt(e.target.value.id),
                                );
                            }}
                        />
                    </div>
                    <div className={styles["group-content"]}>
                        <div className={styles["flex"]}>
                            <div className={styles["input-field"]}>
                                <TextInput
                                    className={styles["input"]}
                                    name={i18n`Created At`}
                                    defaultValue={datetime(CreatedAt) || ""}
                                    disabled
                                />
                            </div>
                            <div className={styles["input-field"]}>
                                <TextInput
                                    className={styles["input"]}
                                    name={i18n`Updated At`}
                                    defaultValue={datetime(UpdatedAt) || ""}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles["commands"]}>
                    <div className={styles["group-content"]}>
                        <div className={styles["input-field"]}>
                            <h5 className={styles["label"]}>{i18n`Entity`}</h5>
                            <Dropdown
                                value={
                                    ruleReferenceType && referenceType.length
                                        ? referenceType.find(
                                              ({ id }) => id === ruleReferenceType,
                                          ) || referenceType[0]
                                        : null
                                }
                                options={referenceType || []}
                                placeholder={""}
                                className={styles["dropdown"]}
                                filter
                                onChange={(e) => {
                                    const defaultObj = [
                                        {
                                            Entity: "",
                                            FileType: "",
                                            AppliesToVersion: [],
                                            AppliesToDataType: null,
                                            RuleExecutionLevel: "",
                                        },
                                    ];
                                    const updateEntity = (
                                        parsedConfig && parsedConfig.length
                                            ? parsedConfig
                                            : [...defaultObj]
                                    ).map((elem) => {
                                        elem["Entity"] = e.target.value.id;
                                        return elem;
                                    });
                                    handleChange(e, "Config", JSON.stringify(updateEntity));
                                }}
                            />
                        </div>
                        <div className={styles["group-content"]}>
                                {userDefined !== 0 ?
                                (buildRuleCommandContainer(ruleContentComponent, parsedContent)) :
                                null
                                }
                            </div>
                            <div className={styles["input-field"]}>
                                <CheckPermission roles={[UserRoles.ADMIN, UserRoles.PP_SUPPORT]}>
                                    <Checkbox
                                        style={{ marginTop: "10px" }}
                                        value={userDefined}
                                        onChange={(e) => {
                                            handleChange(
                                                e,
                                                disableInputs ? "disabled" : "userDefined",
                                                e.target.checked ? 1 : 0,
                                            );
                                        }}
                                    >{i18n`User Rule`}</Checkbox>
                                </CheckPermission>
                            </div>
                            <div className={styles["group-content"]}>
                                {buildRuleMetadataContainer(ruleMetadataItemsComponent, parsedMetadata, localesArray)}
                        </div>
                    </div>
                </div>
                <div>
                    <div className={styles["group-content"]}>
                        {buildRulesComponentContainer(
                            descriptionItems,
                            availableDescriptionLocales,
                            parsedDescriptionByLocale,
                            parsedDescriptionDetailByLocale,
                            parsedDescriptionPreviewByLocale,
                        )}
                    </div>
                </div>
                <div>
                    <div className={styles["group-content"]}>
                        {buildConfigsContainer(parsedConfig)}
                    </div>
                </div>
                <PackageDropdownMultiSelector
                  packages={packages}
                  selectedPackages={selectedPackages}
                  handleChange={handleChange}
                  entitieID={id}
                  containerTitle={"Packages"}
                  entitieKey={"workerTypeId"}
                />
            </div>
        </form>
    ) : null;

    function buildRuleTranslationContainer(
        ruleObj,
        availableLocalesArray,
        localesArray,
        descriptionParsedObj = {},
        descriptionDetailParsedObj = {},
        descriptionPreviewParsedObj = {},
    ) {
        let tempObj = {};
        let tempDescriptionObj = {};
        let tempDescriptionPreviewObj = {};
        let tempDescriptionDetailObj = {};
        return (typeof ruleObj === "object" ? Object.keys(ruleObj) : []).map((locale, index) => {
            return (
                <div key={index} className={styles["rule-entry"]}>
                    <div className={styles["locale-container"]}>
                        <Dropdown
                            placeholder={""}
                            className={styles["period-dropdown"]}
                            secondary
                            options={availableLocalesArray || []}
                            value={localesArray.find(({ id }) => id === locale)}
                            onChange={(e) => {
                                descriptionParsedObj[e.target.value.id] =
                                    descriptionParsedObj[locale] || "";
                                delete descriptionParsedObj[locale];
                                descriptionDetailParsedObj[e.target.value.id] =
                                    descriptionDetailParsedObj[locale] || "";
                                delete descriptionDetailParsedObj[locale];
                                descriptionPreviewParsedObj[e.target.value.id] =
                                    descriptionPreviewParsedObj[locale] || "";
                                delete descriptionPreviewParsedObj[locale];
                                tempDescriptionObj = { ...descriptionParsedObj };
                                tempDescriptionDetailObj = { ...descriptionDetailParsedObj };
                                tempDescriptionPreviewObj = {
                                    ...descriptionPreviewParsedObj,
                                };
                                tempObj = {
                                    Description: { ...tempDescriptionObj },
                                    DescriptionDetail: { ...tempDescriptionDetailObj },
                                    DescriptionPreview: { ...tempDescriptionPreviewObj },
                                };
                                handleGlobalObjectChange(e, tempObj);
                            }}
                            i18n={i18n}
                        />
                        <div className={styles["remove-button-container"]}>
                            <button
                                type="button"
                                className={styles["remove-button"]}
                                onClick={(e) => {
                                    descriptionParsedObj &&
                                        delete descriptionParsedObj[locale || ""];
                                    tempDescriptionObj = { ...(descriptionParsedObj || {}) };
                                    descriptionDetailParsedObj &&
                                        delete descriptionDetailParsedObj[locale || ""];
                                    tempDescriptionDetailObj = {
                                        ...(descriptionDetailParsedObj || {}),
                                    };
                                    descriptionPreviewParsedObj &&
                                        delete descriptionPreviewParsedObj[locale || ""];
                                    tempDescriptionPreviewObj = {
                                        ...(descriptionPreviewParsedObj || {}),
                                    };
                                    tempObj = {
                                        Description: { ...tempDescriptionObj },
                                        DescriptionDetail: { ...tempDescriptionDetailObj },
                                        DescriptionPreview: { ...tempDescriptionPreviewObj },
                                    };
                                    handleGlobalObjectChange(e, tempObj);
                                }}
                            >
                                <Icon name="remove" />
                            </button>
                        </div>
                    </div>
                    <div className={styles["rule-container"]}>
                        <div className={styles["translation-container"]}>
                            <TextArea
                                className={styles["login-text-input"]}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck="false"
                                name={i18n`Title`}
                                placeholder={i18n`Enter a Title`}
                                value={(descriptionParsedObj && descriptionParsedObj[locale]) || ""}
                                onChange={(e) => {
                                    if (typeof ruleObj === "object") {
                                        descriptionParsedObj[locale] = e.target.value;
                                        tempDescriptionObj = { ...descriptionParsedObj };
                                    } else {
                                        tempDescriptionObj[locale] = e.target.value;
                                    }
                                    handleChange(
                                        e,
                                        disableInputs ? "disabled" : `Description`,
                                        JSON.stringify(tempDescriptionObj),
                                    );
                                }}
                            />
                        </div>
                        <div className={styles["translation-container"]}>
                            <TextArea
                                className={styles["login-text-input"]}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck="false"
                                name={i18n`DescriptionPreview`}
                                placeholder={i18n`Enter a Description`}
                                value={
                                    (descriptionPreviewParsedObj &&
                                        descriptionPreviewParsedObj[locale]) ||
                                    ""
                                }
                                onChange={(e) => {
                                    if (typeof ruleObj === "object") {
                                        descriptionPreviewParsedObj[locale] = e.target.value;
                                        tempDescriptionPreviewObj = {
                                            ...descriptionPreviewParsedObj,
                                        };
                                    } else {
                                        tempDescriptionPreviewObj[locale] = e.target.value;
                                    }
                                    handleChange(
                                        e,
                                        disableInputs ? "disabled" : `DescriptionPreview`,
                                        JSON.stringify(tempDescriptionPreviewObj),
                                    );
                                }}
                            />
                        </div>
                        <div className={styles["translation-container"]}>
                            <TextArea
                                className={styles["login-text-input"]}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck="false"
                                name={i18n`DescriptionDetail`}
                                placeholder={i18n`Enter a Error Message`}
                                value={
                                    (descriptionDetailParsedObj &&
                                        descriptionDetailParsedObj[locale]) ||
                                    ""
                                }
                                onChange={(e) => {
                                    if (typeof ruleObj === "object") {
                                        descriptionDetailParsedObj[locale] = e.target.value;
                                        tempDescriptionDetailObj = {
                                            ...descriptionDetailParsedObj,
                                        };
                                    } else {
                                        tempDescriptionDetailObj[locale] = e.target.value;
                                    }
                                    handleChange(
                                        e,
                                        disableInputs ? "disabled" : `DescriptionDetail`,
                                        JSON.stringify(tempDescriptionDetailObj),
                                    );
                                }}
                            />
                        </div>
                    </div>
                </div>
            );
        });
    }

    function buildRulesComponentContainer(
        itemsArray,
        availableLocalesArray,
        handleDescriptionObj,
        handleDescriptionDetailObj,
        handleDescriptionPreviewObj,
    ) {
        let tempObj = {};
        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <div>
                        {(itemsArray || []).length ? (
                            itemsArray
                        ) : (
                            <small>{i18n`No descriptions assigned`}</small>
                        )}
                    </div>
                    <div>
                        <button
                            className={styles["add-button"]}
                            type="button"
                            onClick={(e) => {
                                tempObj = {
                                    Description: { ...handleDescriptionObj, ...{ [availableLocalesArray[0]["id"]]: "" } },
                                    DescriptionDetail: {
                                        ...handleDescriptionDetailObj,
                                        ...{ [availableLocalesArray[0]["id"]]: "" },
                                    },
                                    DescriptionPreview: {
                                        ...handleDescriptionPreviewObj,
                                        ...{ [availableLocalesArray[0]["id"]]: "" },
                                    },
                                };
                                handleGlobalObjectChange(e, tempObj);
                            }}
                        >
                            {availableLocalesArray && availableLocalesArray.length ? (
                                <small>{i18n`[+] Add new Description`}</small>
                            ) : null}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    function buildConfigsContainer(parsedConfig) {
        const defaultObj = {
            Entity:
                parsedConfig && parsedConfig.length && parsedConfig[0]["Entity"]
                    ? parsedConfig[0]["Entity"]
                    : "",
            FileType: "",
            AppliesToVersion: [],
            AppliesToDataType: null,
            RuleExecutionLevel: "",
        };
        return (
            <div>
                <div className={styles["flex"]}>
                    <div className={styles["input-field"]}>
                        <div>
                            {(parsedConfig || []).length ? (
                                ruleConfigurations
                            ) : (
                                <small>{i18n`No configurations assigned`}</small>
                            )}
                        </div>
                        <div>
                            <button
                                className={styles["add-button"]}
                                type="button"
                                onClick={(e) => {
                                    handleChange(
                                        e,
                                        "Config",
                                        JSON.stringify([
                                            ...(parsedConfig || []),
                                            { ...defaultObj },
                                        ]),
                                    );
                                }}
                            >
                                {<small>{i18n`[+] Add new Configuration`}</small>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function buildConfigsDataContainer(ruleConfigsArray, appliesToArray) {
        return (
            ruleConfigsArray &&
            ruleConfigsArray.length &&
            ruleConfigsArray.map((elem, index) => {
                const {
                    FileType: ruleFileType,
                    AppliesToVersion: ruleAppliesToVersion,
                    AppliesToDataType: ruleAppliesToDataType,
                    RuleExecutionLevel: ruleLevel,
                } = elem || {};
                const hasAppliesToDataTypes = appliesToArray && appliesToArray.length ? appliesToArray.reduce((acc, {config}) => {
                    const appliesToParsedConfig = JSON.parse(config)
                    if (config && appliesToParsedConfig[ruleFileType]) {
                        const parsedDataTypes = JSON.parse(appliesToParsedConfig[ruleFileType])["dataTypes"]
                        const subTypes = Object.keys(parsedDataTypes) || []
                        subTypes && subTypes.length && subTypes.forEach((label) => {
                            const defaultLocale = Object.keys(parsedDataTypes[label])
                            acc.push({
                                id: label,
                                name: parsedDataTypes[label][actualLocale] || (defaultLocale && defaultLocale.length ? parsedDataTypes[label][defaultLocale[0]] : "N/A")
                            })
                        })
                    }
                    return acc;
                }, []) : []
                return (
                    <div key={index} className={styles["rule-entry"]}>
                        <div className={styles["config-remove-button-container"]}>
                            <div className={styles["remove-button-container"]}>
                                <button
                                    type="button"
                                    className={styles["remove-button"]}
                                    onClick={(e) => {
                                        ruleConfigsArray.splice(index, 1);
                                        handleChange(e, "Config", JSON.stringify(ruleConfigsArray));
                                    }}
                                >
                                    <Icon name="remove" />
                                </button>
                            </div>
                        </div>
                        <div className={styles["flex"]}>
                            <div className={styles["input-field"]}>
                                <h5 className={styles["label"]}>{i18n`Applies To`}</h5>
                                <Dropdown
                                    value={
                                        ruleFileType && appliesTo.length
                                            ? appliesTo.find(({ id }) => id === ruleFileType) ||
                                              appliesTo[0]
                                            : null
                                    }
                                    useI18n={false}
                                    options={availableAppliesTo || []}
                                    placeholder={""}
                                    className={styles["dropdown"]}
                                    filter
                                    onChange={(e) => {
                                        handleConfigsObjectChange(
                                            e,
                                            "FileType",
                                            e.target.value.id,
                                            index,
                                            ruleConfigsArray,
                                        );
                                    }}
                                />
                            </div>
                        </div>
                        <div className={styles["flex"]}>
                            {isToShowAppliesTo(hasAppliesToDataTypes) && (
                                <div className={styles["input-field"]}>
                                    <div className={styles["input-field"]}>
                                        <MultiSelectionInput
                                            className={styles["input"]}
                                            name={i18n`Applies to Data Types`}
                                            value={(typeof ruleAppliesToDataType === "string"
                                                ? JSON.parse(ruleAppliesToDataType || "[]")
                                                : ruleAppliesToDataType || []
                                            )
                                                .map((type) => type)
                                                .filter((p) => p)}
                                            placeholder={i18n`Select applies to data types`}
                                            options={
                                                hasAppliesToDataTypes && hasAppliesToDataTypes.length ?
                                                hasAppliesToDataTypes : []
                                            }
                                            onChange={(e) => {
                                                handleConfigsObjectChange(
                                                    e,
                                                    "AppliesToDataType",
                                                    e.target.value && e.target.value.length
                                                        ? e.target.value
                                                        : null,
                                                    index,
                                                    ruleConfigsArray,
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            {isToShowAppliesTo(hasAppliesToDataTypes) && (
                                <div className={styles["input-field"]}>
                                    <MultiSelectionInput
                                        className={styles["input"]}
                                        name={i18n`Applies to Version`}
                                        value={(typeof ruleAppliesToVersion === "string"
                                            ? JSON.parse(ruleAppliesToVersion || "[]")
                                            : ruleAppliesToVersion || []
                                        )
                                            .map((type) => type)
                                            .filter((p) => p)}
                                        placeholder={i18n`Select applies to version`}
                                        options={
                                            appliesToVersion && appliesToVersion.length
                                                ? appliesToVersion.map(({ id, name }) => ({
                                                      id,
                                                      name: name,
                                                  }))
                                                : []
                                        }
                                        onChange={(e) => {
                                            handleConfigsObjectChange(
                                                e,
                                                "AppliesToVersion",
                                                e.target.value,
                                                index,
                                                ruleConfigsArray,
                                            );
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles["input-field"]}>
                            <h5 className={styles["label"]}>{i18n`Execute On`}</h5>
                            <Dropdown
                                value={
                                    ruleLevel && level.length
                                        ? level.find(({ id }) => id === ruleLevel)
                                        : null
                                }
                                placeholder={""}
                                options={level || []}
                                className={styles["dropdown"]}
                                filter
                                onChange={(e) => {
                                    handleConfigsObjectChange(
                                        e,
                                        "RuleExecutionLevel",
                                        e.target.value.id,
                                        index,
                                        ruleConfigsArray,
                                    );
                                }}
                            />
                        </div>
                    </div>
                );
            })
        );
    }

    function buildRuleMetadataTranslationContainer(ruleMetadata, localesArray) {
        return (
            <div>
                <div className={`${styles["metadata-entry"]} ${styles["scrollable-metadata-entry"]}`}>
                    {(ruleMetadata || []).map((columnMetadata, index) => {
                        const { key: column, type, translations } = columnMetadata || {}
                        const availableLocalesArray = availableLocales(localesArray, (translations || []).reduce((acc, {lang, description}) => {
                            if (!acc[lang]) {
                                acc[lang.split("_")[0]] = description
                            }
                            return acc
                        }, {}))
                        return (
                            <div
                                ref={refs[index]}
                                key={`${index}`}
                                className={`${styles["metadata-container"]} ${styles["darker-border"]}`}
                            >
                                <div
                                    className={`${styles["remove-button-container"]} ${styles["flex-end"]}`}
                                >
                                    <button
                                        type="button"
                                        className={styles["remove-button"]}
                                        onClick={(e) => {
                                            delete ruleMetadata[index];
                                            setEventType("stay")
                                            handleGlobalObjectChange(e, {
                                                Metadata: { ...arrayToJson(ruleMetadata) },
                                            });
                                        }}
                                    >
                                        <Icon name="remove" />
                                    </button>
                                </div>
                                <div className={styles["flex"]}>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Column`}
                                            placeholder={i18n`Enter a column value`}
                                            value={column.split("_")[0] || ""}
                                            onKeyDown={(e) => e.keyCode === 32 && e.preventDefault()}
                                            onChange={(e) => {
                                                delete ruleMetadata[index];
                                                ruleMetadata[index] = { ...columnMetadata, key: e.target.value }
                                                setEventType("stay")
                                                handleGlobalObjectChange(e, {
                                                    Metadata: { ...arrayToJson(ruleMetadata) },
                                                });
                                            }}
                                        />
                                    </div>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Type`}
                                            placeholder={i18n`Enter a type value`}
                                            value={type || ""}
                                            onChange={(e) => {
                                                ruleMetadata[index]["type"] = e.target.value;
                                                setEventType("stay")
                                                handleGlobalObjectChange(e, {
                                                    Metadata: { ...arrayToJson(ruleMetadata) },
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                                {(translations || []).map(
                                    (locale, localeIndex) => {
                                        const {lang, description} = locale || {};
                                        return (
                                            <div
                                                key={`${localeIndex}`}
                                                className={`${styles["flex"]}`}
                                            >
                                                <div
                                                    className={`${styles["translation-container"]} ${styles["translation"]}`}
                                                >
                                                    <div
                                                        className={
                                                            styles["locale-and-remove-button"]
                                                        }
                                                    >
                                                        <Dropdown
                                                            style={{ marginBottom: "1rem" }}
                                                            placeholder={""}
                                                            className={styles["period-dropdown"]}
                                                            secondary
                                                            options={availableLocalesArray || []}
                                                            value={localesArray.find(
                                                                ({ id }) => id === lang.split("_")[0],
                                                            )}
                                                            onChange={(e) => {
                                                                if (columnMetadata && columnMetadata["translations"] && columnMetadata["translations"][localeIndex]) {
                                                                    const oldDescription = columnMetadata["translations"][localeIndex]["description"];
                                                                    delete columnMetadata["translations"][localeIndex]
                                                                    columnMetadata["translations"][localeIndex] = {
                                                                        lang: e.target.value.id,
                                                                        description: oldDescription
                                                                    }
                                                                }
                                                                setEventType("stay")
                                                                handleGlobalObjectChange(e, {
                                                                    Metadata: {
                                                                        ...arrayToJson(
                                                                            ruleMetadata,
                                                                        ),
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                        <div
                                                            className={`${styles["config-remove-button-container"]} ${styles["adjust"]}`}
                                                        >
                                                            <div
                                                                className={`${styles["remove-button-container"]}`}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={
                                                                        styles["remove-button"]
                                                                    }
                                                                    onClick={(e) => {
                                                                        if (columnMetadata && columnMetadata["translations"] && columnMetadata["translations"][localeIndex]) {
                                                                            delete columnMetadata["translations"][localeIndex];
                                                                        }
                                                                        setEventType("stay")
                                                                        handleGlobalObjectChange(
                                                                            e,
                                                                            {
                                                                                Metadata: {
                                                                                    ...arrayToJson(
                                                                                        ruleMetadata,
                                                                                    ),
                                                                                },
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    <Icon name="remove" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <TextInput
                                                        className={styles["input"]}
                                                        name={i18n`Column Description`}
                                                        placeholder={i18n`Enter a column description`}
                                                        value={description || ""}
                                                        onChange={(e) => {
                                                            if (translations) {
                                                                translations[localeIndex]["description"] = e.target.value;
                                                            }
                                                            setEventType("stay")
                                                            handleGlobalObjectChange(e, {
                                                                Metadata: {
                                                                    ...arrayToJson(ruleMetadata),
                                                                },
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    },
                                )}
                                {availableLocalesArray && availableLocalesArray.length ? (
                                    <div
                                        className={`${styles["add-rule-metadata"]} ${styles["flex-end-metadata"]}`}
                                    >
                                        <button
                                            className={styles["add-button"]}
                                            type="button"
                                            onClick={(e) => {
                                                const defaultObj = {
                                                    lang: availableLocalesArray[0]["id"],
                                                    description: ""
                                                };
                                                if (columnMetadata && columnMetadata["translations"]) {
                                                    columnMetadata["translations"].push({...defaultObj})
                                                } else {
                                                    ruleMetadata[index] = {
                                                        key: column,
                                                        type,
                                                        translations: [{...defaultObj}]
                                                    }
                                                }
                                                setEventType("stay")
                                                handleGlobalObjectChange(e, {
                                                    Metadata: { ...arrayToJson(ruleMetadata) },
                                                });
                                            }}
                                        >
                                            <small>{i18n`[+] Add new column metadata`}</small>
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function buildRuleMetadataContainer(ruleItemsObj, handleMetadataObj, localesArray) {
        let tempObj = {};
        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <h5 className={`${styles["label"]} ${styles["no-bottom-margin"]}`}>{i18n`Metadata`}</h5>
                    <div >
                        {(handleMetadataObj || []).length ? (
                            ruleItemsObj
                        ) : (
                            <small>{i18n`No rule metadata assigned`}</small>
                        )}
                    </div>
                    <div>
                        <button
                            className={styles["add-button"]}
                            type="button"
                            onClick={(e) => {
                                tempObj = {
                                    Metadata: {
                                        ...arrayToJson(handleMetadataObj),
                                        ...{
                                            "": {
                                                type: "",
                                                translations: { [localesArray[0]["id"]]: "" },
                                            },
                                        },
                                    },
                                };
                                handleGlobalObjectChange(e, tempObj);
                                setEventType("scrollToContainer")
                            }}
                        >
                            <small>{i18n`[+] Add new metadata`}</small>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    function buildRuleContentContainer(parsedContent, qSelector, functionTypeOptions) {
        const { commands } = parsedContent || {};
        return (
            <div>
                <div
                    className={styles["command-content-entry"]}
                >
                    {(commands || []).filter((elem) => elem !== null).map(({ command = {} }, index) => {
                        return (
                            <div
                                key={`${index}`}
                                className={`${styles["content-container"]} ${styles["darker-border"]} ${styles["scrollable-container"]}`}
                            >
                                <div className={`${styles["remove-button-container"]} ${styles["flex-end"]} ${styles["selectors-remove-button"]}`}>
                                    <button
                                        type="button"
                                        className={styles["remove-button"]}
                                        onClick={(e) => {
                                            const tempObj = parsedContent
                                            delete tempObj["commands"][index]
                                            handleGlobalObjectChange(e, {
                                                Content: { ...tempObj },
                                            });
                                        }}
                                    >
                                        <Icon name="remove" />
                                    </button>
                                </div>
                                <div className={`${styles["query-content-container"]}`}>
                                {(qSelector || []).map(({id, name}, queryIndex) => {
                                    return (
                                        <div key={queryIndex} >
                                            <div className={styles["query-content"]}>
                                                <div className={`${styles["input-field"]} ${styles["query-key"]}`}>
                                                    <TextInput
                                                        style={{ width: '7.5rem', marginTop: '0.4rem' }}
                                                        value={name}
                                                        disabled
                                                    />
                                                </div>
                                                <div className={`${styles["input-field"]} ${styles["query-input"]}`}>
                                                    <TextArea
                                                        className={styles["input"]}
                                                        placeholder={i18n`Enter a value`}
                                                        value={(command && command["query"] && command["query"][`${id}`]) || ""}
                                                        onChange={(e) => {
                                                            const tempObj = parsedContent
                                                            tempObj["commands"][index]["command"]["query"][`${id}`] = e.target.value
                                                            handleGlobalObjectChange(e, {
                                                                Content: { ...tempObj },
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {(commands || []).map(({command}, functionIndex) => {
                                    const { execute_function: functionType } = command || {}
                                    return (
                                        <div key={functionIndex} className={`${styles["function-margin"]}`}>
                                            <div className={styles["query-content"]}>
                                                <div className={`${styles["input-field"]} ${styles["query-key"]}`}>
                                                    <TextInput
                                                        style={{ width: '7.5rem'}}
                                                        value={i18n`Function`}
                                                        disabled
                                                    />
                                                </div>
                                                <div className={`${styles["input-field"]} ${styles["query-input"]}`}>
                                                    <Dropdown
                                                        value={functionType && functionTypeOptions.length ? (functionTypeOptions || []).find(({name}) => name ===  functionType) || functionTypeOptions[0] : null}
                                                        options={functionTypeOptions}
                                                        placeholder={""}
                                                        className={styles["dropdown"]}
                                                        onChange={(e) => {
                                                            const tempObj = parsedContent
                                                            tempObj["commands"][index]["command"]["execute_function"] = e.target.value.id
                                                            handleGlobalObjectChange(e, {
                                                                Content: { ...tempObj },
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function buildRuleCommandContainer(commandItemsObj, handleContentObj) {
        const { commands } = handleContentObj || {}
        let tempObj = {}
        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <h5 className={`${styles["label"]} ${styles["no-bottom-margin"]}`}>{i18n`Content`}</h5>
                    <div>
                        {hasContent(handleContentObj, commands) ? (
                            commandItemsObj
                        ) : (
                            <small>{i18n`No rule content assigned`}</small>
                        )}
                    </div>
                    {!hasContent(handleContentObj, commands) ?
                        <div>
                            <button
                                className={styles["add-button"]}
                                type="button"
                                onClick={(e) => {
                                    tempObj = {
                                        Content: {
                                            ...handleContentObj,
                                            ...{
                                                commands: [
                                                    {
                                                        command: {
                                                            query: {}
                                                        }
                                                    }
                                                ]
                                            }
                                        },
                                    };
                                    handleGlobalObjectChange(e, tempObj);
                                }}
                            >
                                <small>{i18n`[+] Add new content`}</small>
                            </button>
                        </div>
                     : null
                    }
                </div>
            </div>
        )
    }

    function hasContent(content, commands) {
        return content && commands && commands.filter((elem) => elem !== null).length
    }

    function availableLocales(localeArray, currentUsedLocales) {
        return (localeArray || []).filter(
            ({ id }) => !Object.keys(currentUsedLocales || {}).includes(id)
        );
    }

    function arrayToJson(inputArray) {
        return inputArray.reduce((acc, config, index) => {
            const { key, ...rest } = config;

            if (config.translations) {
                const translations = {};

                config.translations.forEach((translation) => {
                    const { lang, description: trans } = translation;
                    translations[`${lang.split("_")[0]}_${index}`] = trans;
                });

                acc[`${key.split("_")[0]}_${index}`] = {
                    ...rest,
                    translations,
                };
            } else {
                acc[`${key.split("_")[0]}_${index}`] = { ...rest };
            }
            return acc;
        }, {});
    }

    function jsonToArray(jsonInput) {
        return Object.entries(jsonInput).map(([key, value], index) => {
            if (value.translations) {
                const translations = Object.entries(value.translations).map(
                    ([lang, translation], langIndex) => ({
                        lang: `${lang.split("_")[0]}_${langIndex}`,
                        description: translation,
                    }),
                );
                return {
                    key: `${key.split("_")[0]}_${index}`,
                    ...value,
                    translations,
                };
            } else {
                return {
                    key: `${key.split("_")[0]}_${index}`,
                    ...value,
                };
            }
        });
    }
};

function statusCheck(modalStatus) {
    return modalStatus !== "loading";
}

function isToShowAppliesTo(appliesToValueArray) {
    return !!(appliesToValueArray && appliesToValueArray.length)
}

const RuleFormRef = forwardRef(NcRuleForm);

RuleFormRef.queries = (args) => {
    return [
        {
            resource: "classifiers",
            args: {
                locale: args.locale
            },
            body: `
				severity{id, name},
				level{id, name},
				referenceType{id, name},
				appliesToDataType{id, name},
				appliesTo{id, name, config},
				appliesToVersion{id, name},
                functionType{id, name}
			`,
        },
        {
            resource: "query_selectors",
            body: 'items{id, name}',
            args: {
                type: "ncRules"
            }
        },
        {
            resource: "getPackages",
            body: "id,name",
        },
        args && args.id
            ? {
                  resource: "nc_rule",
                  args: {
                    ruleId: args.id
                 },
                  body: "__typename,id,RuleID,RuleType,Severity,Content,Description,DescriptionPreview,DescriptionDetail,Entity,AppliesTo,AppliesToVersion,CreatedAt,UpdatedAt,Metadata,userDefined,Config, Packages{id, packageId, workerTypeId}",
              }
            : undefined,
    ].filter((q) => q);
};

RuleFormRef.dataMapper = ["classifiers", "querySelectors" , "packages","data"];

RuleFormRef.actions = [FORM_ACTIONS.SAVE];

export default RuleFormRef;
